/**
 * localStorage-backed session ID persistence.
 *
 * Prevents duplicate D1 session rows when the user navigates back to the
 * funnel after a handoff redirect (browser Back button), a page refresh,
 * or an auth round-trip. On return the persisted session ID is reused
 * instead of calling `POST /api/funnel/sessions` again.
 *
 * Scoped by `funnelId:overrideId` (same key axis as useFunnelDraft) plus
 * `anonymousId` to avoid cross-user leakage on shared devices. Cleared
 * alongside the funnel draft on handoff completion.
 */

const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — matches draft TTL

type PersistedSession = {
  sessionId: string;
  anonymousId: string;
  savedAt: number;
};

const buildKey = (funnelId: string, overrideId: string | null): string =>
  `nextday:session:${funnelId}:${overrideId ?? "_base"}`;

const isPersistedSession = (candidate: unknown): candidate is PersistedSession => {
  if (typeof candidate !== "object" || candidate === null) return false;
  const v = candidate as Partial<PersistedSession>;
  return (
    typeof v.sessionId === "string" &&
    typeof v.anonymousId === "string" &&
    typeof v.savedAt === "number"
  );
};

export const readPersistedSession = (
  funnelId: string,
  overrideId: string | null,
  anonymousId: string,
): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(buildKey(funnelId, overrideId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPersistedSession(parsed)) return null;
    if (parsed.anonymousId !== anonymousId) {
      window.localStorage.removeItem(buildKey(funnelId, overrideId));
      return null;
    }
    if (Date.now() - parsed.savedAt > SESSION_TTL_MS) {
      window.localStorage.removeItem(buildKey(funnelId, overrideId));
      return null;
    }
    return parsed.sessionId;
  } catch {
    return null;
  }
};

export const writePersistedSession = (
  funnelId: string,
  overrideId: string | null,
  sessionId: string,
  anonymousId: string,
): void => {
  if (typeof window === "undefined") return;
  try {
    const entry: PersistedSession = { sessionId, anonymousId, savedAt: Date.now() };
    window.localStorage.setItem(buildKey(funnelId, overrideId), JSON.stringify(entry));
  } catch {
    // Quota exceeded or disabled — silently degrade.
  }
};

export const clearPersistedSession = (funnelId: string, overrideId: string | null): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(buildKey(funnelId, overrideId));
  } catch {
    // ignore
  }
};
